import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Building2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface CompanyAvatarProps {
  src?: string | null;
  alt?: string;
  fallbackText?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  name?: string;
  singleInitial?: boolean;
}

// Generate initials from company name
const getCompanyInitials = (name?: string, singleInitial?: boolean): string => {
  if (!name || !name.trim()) return "";
  
  const words = name.trim().split(' ');
  
  // If single initial is requested, just return first letter
  if (singleInitial) {
    return words[0].charAt(0).toUpperCase();
  }
  
  // For regular behavior, use first two words
  if (words.length >= 2) {
    const initials = words[0].charAt(0) + words[1].charAt(0);
    // Auto-detect if we should use single initial for very long company names
    if (words.length > 4 || name.length > 40) {
      return words[0].charAt(0).toUpperCase();
    }
    return initials.toUpperCase();
  }
  return words[0].charAt(0).toUpperCase();
};

// Generate a professional color based on company name
const getCompanyColor = (name?: string): string => {
  if (!name) return "hsl(var(--muted))";
  
  // Professional company color palette - slightly different from user avatars
  const colors = [
    "hsl(215 20% 32%)", // corporate navy
    "hsl(210 18% 36%)", // professional blue
    "hsl(225 15% 40%)", // executive gray
    "hsl(200 16% 34%)", // business steel
    "hsl(235 12% 38%)", // corporate slate
    "hsl(208 20% 42%)", // professional teal
    "hsl(220 18% 44%)", // muted indigo
    "hsl(195 14% 46%)", // corporate blue-gray
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
};

export function CompanyAvatar({ 
  src, 
  alt = "Company logo", 
  fallbackText,
  className,
  size = "md",
  name,
  singleInitial = true
}: CompanyAvatarProps) {
  const sizeClasses = {
    sm: "h-8 w-8 text-xs",
    md: "h-10 w-10 text-sm", 
    lg: "h-20 w-20 text-base transition-transform duration-200 hover:scale-105"
  };

  const initials = fallbackText || getCompanyInitials(name, singleInitial);
  const backgroundColor = getCompanyColor(name);

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      <AvatarImage 
        src={src || undefined} 
        alt={alt || `${name} logo`}
      />
      <AvatarFallback className="bg-gradient-to-br from-primary/10 to-accent/5">
        {initials ? (
          <span className="font-medium text-primary">
            {initials}
          </span>
        ) : (
          <Building2 className={`${size === 'lg' ? 'h-10 w-10' : 'h-4 w-4'} text-primary`} />
        )}
      </AvatarFallback>
    </Avatar>
  );
}